import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Grid, Group, Paper, Select, Stack, Tabs,
  Text, TextInput, Textarea, Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArrowLeft, IconDeviceMobile, IconEye, IconMail, IconTrash,
} from '@tabler/icons-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  useDeleteTemplate, usePreviewTemplate, useSaveTemplate, useSetDLTStatus, useTemplate,
} from '../api/hooks';
import { PageHeader } from '../components/PageHeader';
import { ApiRequestError } from '../api/client';
import type { Channel } from '../api/types';

export function TemplateEditorPage() {
  const { id = 'new' } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const { data: existing } = useTemplate(id);
  const save = useSaveTemplate();
  const remove = useDeleteTemplate();
  const preview = usePreviewTemplate();
  const setDLT = useSetDLTStatus();

  const [name, setName] = useState('');
  const [channel, setChannel] = useState<Channel>('email');
  const [category, setCategory] = useState('marketing');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [body, setBody] = useState('');
  const [html, setHtml] = useState('');
  const [dltTemplateId, setDltTemplateId] = useState('');
  const [dltSenderId, setDltSenderId] = useState('');
  const [providerTemplateName, setProviderTemplateName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    setName(existing.name);
    setChannel(existing.channel);
    setCategory(existing.category);
    setSubject(existing.subject ?? '');
    setPreheader(existing.preheader ?? '');
    setBody(existing.body);
    setHtml(existing.html ?? '');
    setDltTemplateId(existing.dlt_template_id ?? '');
    setDltSenderId(existing.dlt_sender_id ?? '');
    setProviderTemplateName(existing.provider_template_name ?? '');
  }, [existing]);

  const isEmail = channel === 'email';
  const isSMS = channel === 'sms';
  const isWhatsApp = channel === 'whatsapp';

  // Every {{ … }} the author has used, so they can see at a glance which
  // variables this template depends on before it goes to 50,000 people.
  const variables = useMemo(() => {
    const found = new Set<string>();
    for (const src of [subject, body, html]) {
      for (const m of src.matchAll(/\{\{\s*([\w.]+)/g)) {
        if (m[1]) found.add(m[1]);
      }
    }
    return [...found].sort();
  }, [subject, body, html]);

  function runPreview() {
    setError(null);
    preview.mutate(
      { subject, preheader, body, html, channel, category },
      {
        onError: (e) =>
          setError(
            e instanceof ApiRequestError
              ? [e.message, ...Object.values(e.fieldErrors())].join(' — ')
              : 'Preview failed',
          ),
      },
    );
  }

  function submit() {
    setError(null);
    save.mutate(
      {
        name, channel, category, subject, preheader, body, html,
        dlt_template_id: dltTemplateId,
        dlt_sender_id: dltSenderId,
        provider_template_name: providerTemplateName,
        dlt_status: existing?.dlt_status ?? 'not_required',
      },
      {
        onSuccess: (t) => {
          notifications.show({ message: `Saved ${t.name} (v${t.version})`, color: 'teal' });
          navigate(`/templates/${t.id}`);
        },
        onError: (e) =>
          setError(
            e instanceof ApiRequestError
              ? [e.message, ...Object.values(e.fieldErrors())].join(' — ')
              : 'Save failed',
          ),
      },
    );
  }

  return (
    <>
      <PageHeader
        title={isNew ? 'New template' : name || 'Template'}
        description="Liquid, rendered per recipient. Preview against a synthetic profile before sending."
        action={
          <Group gap="xs">
            <Anchor component={Link} to="/templates" size="sm">
              <Group gap={4}><IconArrowLeft size={14} /> All templates</Group>
            </Anchor>
            {!isNew && (
              <Button
                variant="subtle" color="red" size="xs" leftSection={<IconTrash size={14} />}
                onClick={() =>
                  modals.openConfirmModal({
                    title: 'Delete this template?',
                    children: (
                      <Text size="sm">
                        Journeys referencing it will fail validation the next time
                        they are activated, rather than sending empty messages.
                      </Text>
                    ),
                    labels: { confirm: 'Delete', cancel: 'Keep' },
                    confirmProps: { color: 'red' },
                    onConfirm: () => remove.mutate(id, { onSuccess: () => navigate('/templates') }),
                  })
                }
              >
                Delete
              </Button>
            )}
          </Group>
        }
      />

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Stack>
            <Card>
              <Stack>
                <Group grow>
                  <TextInput
                    label="Name"
                    placeholder="order-shipped"
                    description="Used to reference this template from the API"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                  />
                  <Select
                    label="Channel"
                    data={['push', 'email', 'sms', 'whatsapp']}
                    value={channel}
                    onChange={(v) => setChannel((v ?? 'email') as Channel)}
                  />
                </Group>
                <Select
                  label="Category"
                  description="Transactional, billing, and security bypass quiet hours and frequency caps"
                  data={['marketing', 'product', 'transactional', 'billing', 'security']}
                  value={category}
                  onChange={(v) => setCategory(v ?? 'marketing')}
                />
              </Stack>
            </Card>

            <Card>
              <Stack>
                {(isEmail || channel === 'push') && (
                  <TextInput
                    label={isEmail ? 'Subject' : 'Title'}
                    placeholder="Your order is on the way, {{ profile.first_name }}"
                    value={subject}
                    onChange={(e) => setSubject(e.currentTarget.value)}
                  />
                )}
                {isEmail && (
                  <TextInput
                    label="Preheader"
                    description="The grey line after the subject in most inboxes"
                    value={preheader}
                    onChange={(e) => setPreheader(e.currentTarget.value)}
                  />
                )}

                <Textarea
                  label={isEmail ? 'Plain text body' : 'Body'}
                  description={
                    isSMS
                      ? 'For Indian numbers this must match your DLT-registered text exactly, with {#var#} where values go.'
                      : 'Liquid. Variables: profile.*, data.*, brand.*, links.*'
                  }
                  autosize minRows={6}
                  value={body}
                  onChange={(e) => setBody(e.currentTarget.value)}
                  styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                />

                {isEmail && (
                  <Textarea
                    label="HTML body"
                    description="Wrapped in your branding automatically — do not include <html> or a footer."
                    autosize minRows={8}
                    value={html}
                    onChange={(e) => setHtml(e.currentTarget.value)}
                    styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                  />
                )}
              </Stack>
            </Card>

            {(isSMS || isWhatsApp) && (
              <Card>
                <Group justify="space-between" mb="md">
                  <Text fw={500}>Registration</Text>
                  {existing && existing.dlt_status !== 'not_required' && (
                    <Badge
                      color={
                        existing.dlt_status === 'approved' ? 'teal'
                          : existing.dlt_status === 'rejected' ? 'red' : 'yellow'
                      }
                    >
                      {existing.dlt_status}
                    </Badge>
                  )}
                </Group>

                <Alert color="blue" variant="light" mb="md">
                  <Text size="sm">
                    {isSMS
                      ? 'SMS to Indian numbers must use a DLT-registered template and a six-character sender header. This is checked before fan-out, so an unapproved template fails once with a clear message instead of once per recipient.'
                      : 'WhatsApp can only send templates Meta has approved. Free-form text is rejected before it reaches the network.'}
                  </Text>
                </Alert>

                <Stack>
                  {isSMS && (
                    <Group grow>
                      <TextInput
                        label="DLT template ID"
                        value={dltTemplateId}
                        onChange={(e) => setDltTemplateId(e.currentTarget.value)}
                      />
                      <TextInput
                        label="Sender header"
                        description="Exactly six alphanumeric characters"
                        maxLength={6}
                        value={dltSenderId}
                        onChange={(e) => setDltSenderId(e.currentTarget.value)}
                      />
                    </Group>
                  )}
                  {isWhatsApp && (
                    <TextInput
                      label="Approved template name"
                      description="The elementName from your BSP"
                      value={providerTemplateName}
                      onChange={(e) => setProviderTemplateName(e.currentTarget.value)}
                    />
                  )}

                  {!isNew && (
                    <Group>
                      <Button
                        size="xs" variant="light" color="teal"
                        loading={setDLT.isPending}
                        onClick={() =>
                          setDLT.mutate(
                            { id, status: 'approved', dlt_template_id: dltTemplateId, dlt_sender_id: dltSenderId },
                            { onSuccess: () => notifications.show({ message: 'Marked approved', color: 'teal' }) },
                          )
                        }
                      >
                        Mark approved
                      </Button>
                      <Button
                        size="xs" variant="light" color="yellow"
                        onClick={() => setDLT.mutate({ id, status: 'pending' })}
                      >
                        Awaiting approval
                      </Button>
                      <Button
                        size="xs" variant="light" color="red"
                        onClick={() =>
                          modals.open({
                            title: 'Record a rejection',
                            children: <RejectForm onSubmit={(reason) =>
                              setDLT.mutate(
                                { id, status: 'rejected', rejection_reason: reason },
                                { onSuccess: () => modals.closeAll() },
                              )} />,
                          })
                        }
                      >
                        Rejected
                      </Button>
                    </Group>
                  )}
                  <Text size="xs" c="dimmed">
                    Set by hand because the DLT portal has no usable API. Recording it
                    here is what lets the send path fail fast.
                  </Text>
                </Stack>
              </Card>
            )}

            {error && (
              <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
                {error}
              </Alert>
            )}

            <Group>
              <Button
                variant="light" leftSection={<IconEye size={16} />}
                loading={preview.isPending} onClick={runPreview}
              >
                Preview
              </Button>
              <Button loading={save.isPending} disabled={!name || !body} onClick={submit}>
                {isNew ? 'Create template' : 'Save changes'}
              </Button>
            </Group>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Stack>
            {variables.length > 0 && (
              <Card padding="sm">
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
                  Variables used
                </Text>
                <Group gap={6}>
                  {variables.map((v) => (
                    <Badge key={v} variant="light" size="sm" ff="monospace">{v}</Badge>
                  ))}
                </Group>
                <Text size="xs" c="dimmed" mt="xs">
                  A variable that is missing at send time renders empty rather than
                  failing, so check the preview.
                </Text>
              </Card>
            )}

            {preview.data && (
              <Card padding={0}>
                <Tabs defaultValue={isEmail ? 'html' : 'text'}>
                  <Tabs.List>
                    {isEmail && (
                      <Tabs.Tab value="html" leftSection={<IconMail size={14} />}>
                        Rendered
                      </Tabs.Tab>
                    )}
                    <Tabs.Tab value="text" leftSection={<IconDeviceMobile size={14} />}>
                      Text
                    </Tabs.Tab>
                  </Tabs.List>

                  {isEmail && (
                    <Tabs.Panel value="html" p="sm">
                      <Text size="xs" c="dimmed" mb={6}>
                        Subject: <strong>{preview.data.subject}</strong>
                      </Text>
                      {/*
                        Sandboxed and rendered via srcDoc: this is tenant-authored
                        HTML, and putting it in the dashboard's own DOM would make
                        a template an XSS vector against the operator. The sandbox
                        attribute with no allow-scripts is what makes previewing
                        untrusted markup safe.
                      */}
                      <iframe
                        title="Email preview"
                        sandbox=""
                        srcDoc={preview.data.html}
                        style={{
                          width: '100%', height: 520, border: '1px solid var(--mantine-color-default-border)',
                          borderRadius: 8, background: '#fff',
                        }}
                      />
                    </Tabs.Panel>
                  )}

                  <Tabs.Panel value="text" p="sm">
                    {isSMS && (
                      <Group gap="xs" mb="xs">
                        <Tooltip label="Each segment is billed separately">
                          <Badge color={(preview.data.sms_segments ?? 1) > 1 ? 'yellow' : 'gray'}>
                            {preview.data.sms_segments} segment
                            {(preview.data.sms_segments ?? 1) === 1 ? '' : 's'}
                          </Badge>
                        </Tooltip>
                        <Badge variant="light" color={preview.data.sms_unicode ? 'orange' : 'gray'}>
                          {preview.data.sms_unicode ? 'Unicode — 70 chars/segment' : 'GSM-7 — 160 chars/segment'}
                        </Badge>
                        <Text size="xs" c="dimmed">{preview.data.sms_length} chars</Text>
                      </Group>
                    )}
                    <Paper withBorder p="sm" bg="var(--mantine-color-default-hover)">
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} ff="monospace">
                        {preview.data.text || '(empty)'}
                      </Text>
                    </Paper>
                  </Tabs.Panel>
                </Tabs>
              </Card>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </>
  );
}

function RejectForm({ onSubmit }: { onSubmit: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Stack>
      <Textarea
        label="Reason given by the portal"
        autosize minRows={3}
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
        data-autofocus
      />
      <Button color="red" onClick={() => onSubmit(reason)}>Record rejection</Button>
    </Stack>
  );
}
